// Copyright (c) Bentley Systems
import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  Button,
  ComboBox,
  IconButton,
  Tooltip,
} from "@itwin/itwinui-react";
import { SvgInfoCircular } from "@itwin/itwinui-icons-react";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { CatalogGeneralInfo } from "../ProjectManagement/Models/catalog-general-info";
import { Organization } from "./Models/organizations";
import { appendLoadMoreOption, LOAD_MORE_VALUE, loadingSkeleton, paginatedItemRenderer, searchEmptyMessage, searchPlaceholder } from "../Helpers/ComboBoxHelpers";
import { useSubscribeProjectFormApi } from "./useSubscribeProjectFormApi";

export type SubscribeProjectFormProps = {
  orgId?: string;
  closePanelFunction?: (refreshList: boolean) => void;
};

const SubscribeProjectForm = (props: SubscribeProjectFormProps) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const accessToken = useAppSelector(selectAccessToken);

  const onCancelClick = () => {
    closePanel(false);
  };

  const closePanel = (refreshList: boolean) => {
    if (props.closePanelFunction !== undefined) {
      props.closePanelFunction(refreshList);
    }
  };

  const {
    isOrgLoading,
    organizationsFound,
    organizations,
    selectedOrganization,
    handleOrgSelect,
    handleOrgSearchChange,
    fetchMoreOrganizations,
    searchOrgString,
    projects,
    selectedProject,
    handleProjectSelect,
    isProjectsLoading,
    projectsFound,
    projectRawInput,
    projectComboboxInputChange,
    fetchProjects,
    isSaving,
    save,
  } = useSubscribeProjectFormApi({
    accessToken,
    dispatch,
    orgId: props.orgId,
    showToast,
    closePanel,
  });

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    await save();
  };

  const orgEmptyContent = useMemo(() => {
    if (isOrgLoading) { return loadingSkeleton(6); }
    return searchEmptyMessage(searchOrgString, 4, "No organizations found");
  }, [isOrgLoading, searchOrgString]);

  const projectEmptyContent = useMemo(() => {
    if (isProjectsLoading) { return loadingSkeleton(6); }
    return searchEmptyMessage(projectRawInput, 3, "No projects found");
  }, [isProjectsLoading, projectRawInput]);

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      <div style={{ height: "100%" }}>
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="becs-infopanelbody-footer-line"
              style={{
                flex: 1,
                overflowY: "auto",
              }}
            >
              <div className="becs-all-margin">
                {props.orgId === undefined && (
                  <div className="becs-t-margin">
                    <div className="becs-input-grid">
                      <div className="becs-input-label required" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Select organization (showing {organizations.length} of{" "}
                        {organizationsFound} found)
                        <Tooltip
                          content="Searches by prefix — e.g. &quot;Bent&quot; finds &quot;Bentley&quot;."
                          placement="right"
                        >
                          <IconButton
                            styleType="borderless"
                            size="small"
                            aria-label="Search help"
                            style={{ padding: 0, minWidth: "auto", minHeight: "auto" }}
                          >
                            <SvgInfoCircular />
                          </IconButton>
                        </Tooltip>
                      </div>

                      <ComboBox
                        emptyStateMessage={orgEmptyContent}
                        inputProps={{
                          placeholder: searchPlaceholder("Search organization", 4, isOrgLoading),
                          onChange: handleOrgSearchChange,
                        }}
                        filterFunction={(options) => options}
                        options={appendLoadMoreOption(
                          organizations.map((org) => ({
                            label: `${org.Name} (${org.Id})`,
                            value: org,
                          })),
                          organizationsFound > organizations.length,
                        )}
                        onChange={(currentValue) => {
                          if (currentValue !== LOAD_MORE_VALUE) {
                            handleOrgSelect(currentValue);
                          }
                        }}
                        multiple={false}
                        itemRenderer={paginatedItemRenderer<Organization>({
                          onLoadMore: fetchMoreOrganizations,
                          isLoading: isOrgLoading,
                        })}
                      />
                    </div>
                  </div>
                )}
                <div className="becs-t-margin">
                  <div className="becs-input-grid">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className={"becs-input-label required"}>
                        Select project (showing {projects.length} of{" "}
                        {projectsFound} found)
                      </div>
                    </div>
                    <ComboBox
                      inputProps={{
                        placeholder: props.orgId || selectedOrganization
                          ? searchPlaceholder("Search project", 3)
                          : "Select an organization first",
                        onChange: projectComboboxInputChange,
                        disabled: !(props.orgId || selectedOrganization),
                      }}
                      filterFunction={(options) => options}
                      emptyStateMessage={projectEmptyContent}
                      options={appendLoadMoreOption(
                        projects.map((project) => ({
                          label: project.Name ?? "",
                          value: project,
                        })),
                        projectsFound > projects.length && !!(props.orgId || selectedOrganization),
                      )}
                      onChange={(currentValue) => {
                        if (currentValue !== LOAD_MORE_VALUE) {
                          handleProjectSelect(currentValue);
                        }
                      }}
                      itemRenderer={paginatedItemRenderer<CatalogGeneralInfo>({
                        onLoadMore: () => { void fetchProjects(true, projects.length); },
                        isLoading: isProjectsLoading,
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div className="becs-all-margin">
                <div className="rowWrap">
                  <Button
                    key="saveCredentialBtn"
                    as="button"
                    styleType="cta"
                    type="submit"
                    disabled={
                      isSaving ||
                      !(
                        selectedProject &&
                        (props.orgId || selectedOrganization)
                      )
                    }
                  >
                    <div>Subscribe</div>
                  </Button>
                  <Button
                    key="cancelCredentialBtn"
                    as="button"
                    onClick={() => onCancelClick()}
                  >
                    <div>Cancel</div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscribeProjectForm;
